import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Package, MapPin, Truck, CheckCircle2, ExternalLink, RefreshCw, X } from "lucide-react";

const STEPS = [
  { key: "address_requested", label: "Address requested", icon: Package },
  { key: "address_shared", label: "Address shared", icon: MapPin },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];
const ORDER = STEPS.map((s) => s.key);
const inputCls = "mt-1.5 bg-white/[0.02] border-white/10 text-white";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Timeline({ shipping }) {
  const idx = ORDER.indexOf(shipping.status);
  const dates = { address_requested: shipping.requested_at, address_shared: shipping.address_shared_at, shipped: shipping.shipped_at, delivered: shipping.delivered_at };
  return (
    <ol className="grid grid-cols-4 gap-2" data-testid="shipping-timeline">
      {STEPS.map((s, i) => {
        const done = i <= idx;
        const Icon = s.icon;
        return (
          <li key={s.key} className="min-w-0" data-testid={`shipping-step-${s.key}`} data-done={done}>
            <div className={`h-1 rounded-full mb-2 ${done ? (s.key === "delivered" ? "bg-emerald-400" : "nexus-gradient") : "bg-white/10"}`} />
            <div className={`flex items-center gap-1.5 text-[11px] ${done ? "text-white" : "text-zinc-600"}`}>
              <Icon className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{s.label}</span>
            </div>
            {dates[s.key] && <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{fmt(dates[s.key])}</div>}
          </li>
        );
      })}
    </ol>
  );
}

function AddressCard({ a }) {
  return (
    <div className="text-sm text-zinc-200 p-3 rounded-xl bg-white/[0.02] hairline" data-testid="shipping-address-card">
      <div className="font-semibold">{a.name}</div>
      <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
      <div>{a.city} {a.postcode}</div>
      <div className="text-zinc-400">{a.country}{a.phone ? ` · ${a.phone}` : ""}</div>
    </div>
  );
}

function AddressForm({ onSubmit, busy, defaults }) {
  const [a, setA] = useState({ name: defaults?.name || "", line1: defaults?.line1 || "", line2: defaults?.line2 || "", city: defaults?.city || "", postcode: defaults?.postcode || "", country: defaults?.country || "United Kingdom", phone: defaults?.phone || "" });
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const valid = a.name.trim().length >= 2 && a.line1.trim().length >= 3 && a.city.trim() && a.postcode.trim().length >= 2;
  return (
    <div className="space-y-3" data-testid="shipping-address-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Full name</Label><Input value={a.name} onChange={set("name")} className={inputCls} data-testid="ship-addr-name" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Phone (for courier)</Label><Input value={a.phone} onChange={set("phone")} className={inputCls} data-testid="ship-addr-phone" /></div>
      </div>
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Address line 1</Label><Input value={a.line1} onChange={set("line1")} className={inputCls} data-testid="ship-addr-line1" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Address line 2</Label><Input value={a.line2} onChange={set("line2")} className={inputCls} data-testid="ship-addr-line2" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs uppercase tracking-widest text-zinc-500">City</Label><Input value={a.city} onChange={set("city")} className={inputCls} data-testid="ship-addr-city" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Postcode</Label><Input value={a.postcode} onChange={set("postcode")} className={inputCls} data-testid="ship-addr-postcode" /></div>
        <div><Label className="text-xs uppercase tracking-widest text-zinc-500">Country</Label><Input value={a.country} onChange={set("country")} className={inputCls} data-testid="ship-addr-country" /></div>
      </div>
      <Button onClick={() => onSubmit(a)} disabled={busy || !valid} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="ship-addr-submit">
        <MapPin className="h-4 w-4 mr-2" /> Share address with brand
      </Button>
      <p className="text-[11px] text-zinc-500">Only this brand can see your address. It's used for this parcel and nothing else.</p>
    </div>
  );
}

export function ShippingPanel({ hire, isBrand, isCreator, user, onChange }) {
  const shipping = hire.shipping;
  const [busy, setBusy] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [autoTracking, setAutoTracking] = useState(false);
  const [carrier, setCarrier] = useState("royal_mail");
  const [tracking, setTracking] = useState("");
  const [shipNote, setShipNote] = useState("");

  useEffect(() => {
    api.get("/shipping/carriers").then((r) => { setCarriers(r.data.carriers || []); setAutoTracking(!!r.data.auto_tracking); }).catch(() => {});
  }, []);

  const call = async (fn, success) => {
    setBusy(true);
    try { await fn(); success && toast.success(success); await onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const canArrange = ["in_progress", "delivered"].includes(hire.status);
  if (!shipping && !isBrand) return null;
  if (!shipping && !canArrange) return null;

  return (
    <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="shipping-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Package className="h-3.5 w-3.5" /> Product shipping</div>
          <h3 className="font-display text-xl mt-1">
            {!shipping && "Sending a product to the creator?"}
            {shipping?.status === "address_requested" && (isCreator ? "Share your delivery address" : "Waiting for the creator's address")}
            {shipping?.status === "address_shared" && (isBrand ? "Post the parcel and add tracking" : "Address shared — waiting for dispatch")}
            {shipping?.status === "shipped" && "Parcel on its way"}
            {shipping?.status === "delivered" && "Parcel delivered"}
          </h3>
        </div>
        {shipping && shipping.status !== "shipped" && shipping.status !== "delivered" && isBrand && (
          <button onClick={() => call(() => api.delete(`/hires/${hire.id}/shipping`), "Shipping cancelled")} disabled={busy} className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1" data-testid="shipping-cancel-btn">
            <X className="h-3.5 w-3.5" /> Not shipping after all
          </button>
        )}
      </div>

      {!shipping && isBrand && (
        <div className="space-y-3">
          <p className="text-sm text-zinc-400">Request the creator's delivery address, then add the carrier tracking number. Both of you see live delivery status so there's no "did it arrive?" back-and-forth.</p>
          <Button onClick={() => call(() => api.post(`/hires/${hire.id}/shipping/request`, { note: "" }), "Address requested — creator notified")} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="shipping-request-btn">
            <Package className="h-4 w-4 mr-2" /> I'm shipping a product · request address
          </Button>
        </div>
      )}

      {shipping && <Timeline shipping={shipping} />}

      {shipping?.status === "address_requested" && isCreator && (
        <AddressForm busy={busy} defaults={{ name: user?.name, line1: user?.address_line1, line2: user?.address_line2, city: user?.city, postcode: user?.postcode, country: user?.country }} onSubmit={(a) => call(() => api.post(`/hires/${hire.id}/shipping/address`, a), "Address shared — the brand has been notified")} />
      )}
      {shipping?.status === "address_requested" && isBrand && (
        <p className="text-sm text-zinc-400">We've asked the creator for their address. You'll get a notification the moment it lands.</p>
      )}

      {shipping?.address && (isBrand || shipping.status !== "address_requested") && <AddressCard a={shipping.address} />}

      {shipping?.status === "address_shared" && isBrand && (
        <div className="space-y-3" data-testid="shipping-ship-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Carrier</Label>
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="mt-1.5 w-full h-10 rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="ship-carrier-select">
                {carriers.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tracking number</Label>
              <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. JD0002212345678901" className={inputCls} data-testid="ship-tracking-input" />
            </div>
          </div>
          <Input value={shipNote} onChange={(e) => setShipNote(e.target.value)} placeholder="Note for the creator (optional) — what's in the box, sizes, etc." className="bg-white/[0.02] border-white/10 text-white" data-testid="ship-note-input" />
          <Button onClick={() => call(() => api.post(`/hires/${hire.id}/shipping/ship`, { carrier, tracking_number: tracking.trim(), note: shipNote }), "Marked as shipped — creator notified")} disabled={busy || tracking.trim().length < 5} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="ship-submit-btn">
            <Truck className="h-4 w-4 mr-2" /> Mark as shipped
          </Button>
          <p className="text-[11px] text-zinc-500">{autoTracking ? "Nexus checks the carrier automatically and marks the parcel delivered for both of you." : "The creator confirms receipt in one tap once the parcel arrives."}</p>
        </div>
      )}

      {(shipping?.status === "shipped" || shipping?.status === "delivered") && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-white/[0.02] hairline" data-testid="shipping-tracking-card">
            <div className="min-w-0">
              <div className="text-sm text-white font-semibold">{shipping.carrier_label} · <span className="font-mono">{shipping.tracking_number}</span></div>
              <div className="text-xs text-zinc-400 mt-0.5" data-testid="shipping-latest-event">
                {shipping.status === "delivered"
                  ? `Delivered ${fmt(shipping.delivered_at)} · confirmed by ${shipping.confirmed_by === "carrier" ? "carrier" : "creator"}`
                  : `${shipping.latest_event || (shipping.auto_tracking ? "Waiting for the first carrier scan…" : "In transit — creator confirms on arrival")}${shipping.latest_event_location ? ` · ${shipping.latest_event_location}` : ""}`}
              </div>
              {shipping.ship_note && <div className="text-xs text-zinc-500 mt-1">“{shipping.ship_note}”</div>}
            </div>
            <div className="flex items-center gap-2">
              {shipping.tracking_url && (
                <a href={shipping.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-nexus-blue hover:underline" data-testid="shipping-track-link">
                  Track on carrier <ExternalLink className="h-3 w-3" />
                </a>
              )}
              {shipping.status === "shipped" && shipping.auto_tracking && (
                <button onClick={() => call(() => api.post(`/hires/${hire.id}/shipping/refresh`))} disabled={busy} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/5 text-zinc-400" title="Refresh tracking" data-testid="shipping-refresh-btn">
                  <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                </button>
              )}
            </div>
          </div>
          {shipping.status === "shipped" && isCreator && (
            <Button onClick={() => call(() => api.post(`/hires/${hire.id}/shipping/confirm`), "Thanks — the brand knows it arrived")} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="shipping-confirm-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Confirm I've received the parcel
            </Button>
          )}
          {shipping.status === "delivered" && (
            <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-emerald-200 text-sm" data-testid="shipping-delivered-note">
              Product received. {isCreator ? "Time to create — the brand can see it landed." : "The creator has everything they need to start shooting."}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
